export function useBuyingListApi(): BuyingListApi {
  const buyingListStore = useBuyingListStore();
  const defaultPaginationSize = Number(useRuntimeConfig().public.defaultPaginationSize);
  const { ensureAuthenticated } = useDjustAuth();

  /**
   * Fetch a buying list by ID
   */
  const getBuyingListbyId = async (
    params: GetBuyingListRequest,
    useStore = true
  ): Promise<GetBuyingListbyIDResponse | null> => {
    try {
      await ensureAuthenticated();

      // Validate that buyingListId is provided
      const { buyingListId, currency } = params;
      if (!buyingListId) {
        throw new Error('The "buyingListId" parameter is required.');
      }

      // Call API to fetch the buying list by ID
      const { data, error } = await useFetch<GetBuyingListbyIDResponse>(
        `/api/buyinglists/${buyingListId}`,
        {
          query: currency ? { currency } : undefined, // Include currency if provided
          key: `getbuyinglist-${buyingListId}`, // Unique key for caching
        }
      );

      // Handle potential API errors
      if (error.value) {
        console.error('Failed to retrieve buying list:', error.value);
        throw new Error('Failed to retrieve buying list.');
      }

      // Optionally update the store with the fetched buying list
      if (useStore && data.value?.success) {
        buyingListStore.buyinglist.push(data.value.buyingList);
      }

      // Return the buying list response
      return data.value as GetBuyingListbyIDResponse;
    } catch (e) {
      const error = e as ApiError;
      console.error('Failed to retrieve buying list:', error.message || error);
      throw new Error('Failed to retrieve buying list. Please try again later.');
    }
  };

  const getBuyingLists = async (
    params: GetBuyingListsRequest,
    useStore = true
  ): Promise<GetBuyingListsResponse | null> => {
    try {
      await ensureAuthenticated();

      // Make an API call using useFetch
      const { data, error } = await useFetch<GetBuyingListsResponse>('/api/buyinglists', {
        params: {
          currency: params.currency,
          page: params.pageable.page,
          size: params.pageable.size,
          status: params.status ? JSON.stringify(params.status) : undefined,
          type: 'BUYING_LIST',
        },
        key: `buyingLists-${JSON.stringify(params)}`, // Unique key for caching
      });

      // Check for errors
      if (error.value) {
        console.error('Failed to retrieve buying lists:', error.value);
        return null;
      }

      // Optionally update the store with the fetched buyingLists
      if (useStore && data.value?.success) {
        buyingListStore.buyinglist = data.value.buyinglists || [];
      }
      return data.value;
    } catch (e) {
      const error = e as ApiError;
      console.error('Unexpected error while retrieving buying lists:', error);
      return null;
    }
  };

  /**
   * Duplicate a buying list
   */
  const duplicateBuyingList = async (
    params: DuplicateBuyingListRequest,
    useStore = true
  ): Promise<CreateBuyingListResponseSDK | null> => {
    try {
      const data = await addOrUpdateBuyingList(
        {
          actionType: 'create',
          name: params.name,
          currency: params.currency,
          typeId: params.typeId || 'offerPriceId',
          lines: params.lines,
        },
        useStore
      );

      return data?.newBuyingList as CreateBuyingListResponseSDK;
    } catch (error) {
      console.error('Failed to duplicate buying list:', error);
      return null;
    }
  };

  const addOrUpdateBuyingList = async (
    params: AddOrUpdateBuyingListRequest,
    useStore = true
  ): Promise<AddOrUpdateBuyingListResponse | null> => {
    try {
      await ensureAuthenticated();

      if (params.actionType === 'create' && !params.name) {
        throw new Error('Name is required to create a new buying list.');
      }
      if (params.actionType === 'update' && !params.buyingListId) {
        throw new Error('Buying list ID is required to update an existing buying list.');
      }

      let newBuyingList: CreateBuyingListResponseSDK | undefined;
      let buyingListId: string;

      // Handle buying list creation
      if (params.actionType === 'create') {
        const { data, error } = await useFetch<CreateBuyingListResponse>(
          '/api/buyinglists/create',
          {
            method: 'POST',
            body: { name: params.name },
            key: `createbuyinglist-${params.name}`, // Unique key for caching
          }
        );

        newBuyingList = data.value?.buyingList;
        // Optionally update the store with the created buying list
        if (useStore && data.value?.success) {
          buyingListStore.buyinglist.push(newBuyingList);
        }
        // Check for errors
        if (error.value) {
          console.error('Failed to create buying list:', error.value);
          return null;
        }
        buyingListId = data.value?.buyingList.id || '';
      } else {
        // For update, use the provided buyingListId
        buyingListId = params.buyingListId!;
      }

      // Construct lines to update
      const linesToUpdate = params.lines.map((line) => ({
        quantity: line.quantity,
        updateAction: line.updateAction,
        ...(line.offerPriceId
          ? { offerPriceId: line.offerPriceId }
          : { productVariantId: line.variantId }),
      }));

      const { data, error } = await useFetch<UpdateBuyingListItemsResponse>(
        `/api/buyinglists/${buyingListId}/items/update`,
        {
          method: 'PUT',
          body: { linesToUpdate },
          key: `updatebuyinglist-${Math.random()}`, // Unique key for caching
        }
      );

      // Check for errors
      if (error.value) {
        console.error('Failed to update buying list:', error.value);
        return null;
      }

      console.log('Products successfully added to buying list');
      return {
        success: data.value?.success || false,
        message: data.value?.message || '',
        newBuyingList,
      };
    } catch (e) {
      const error = e as ApiError;
      console.error('Unexpected error while creating or updating buying list:', error);
      throw error; // Re-throw error for external handling
    }
  };

  /**
   * Create a new buying list
   */
  const createBuyingList = async (
    params: CreateBuyingListRequest,
    useStore = true
  ): Promise<CreateBuyingListResponse | null> => {
    try {
      await ensureAuthenticated();

      // Make an API call using useFetch
      const { data, error } = await useFetch<CreateBuyingListResponse>('/api/buyinglists/create', {
        method: 'POST',
        body: params,
        key: `createbuyinglist-${params.name}`, // Unique key for caching
      });

      // Check for errors
      if (error.value) {
        console.error('Failed to create buying list:', error.value);
        return null;
      }

      // Optionally update the store with the created buying list
      if (useStore && data.value?.success) {
        buyingListStore.addBuyinglist(data.value.buyingList);
      }

      return data.value || null;
    } catch (e) {
      const error = e as ApiError;
      console.error('Unexpected error while creating buying list:', error);
      return null;
    }
  };

  /**
   * Update a buying list by its ID
   */
  const updateBuyingList = async (
    params: UpdateBuyingListRequest,
    useStore = true
  ): Promise<void> => {
    try {
      await ensureAuthenticated();

      // Validate parameters
      const { buyingListId, name } = params;
      if (!buyingListId || !name) {
        throw new Error('Both "buyingListId" and "name" parameters are required.');
      }

      // Call the API to update the buying list
      const { data, error } = await useFetch(`/api/buyinglists/${buyingListId}/update`, {
        method: 'PUT',
        body: { name },
        key: `updatebuyinglist-${buyingListId}-${params}`, // Unique key for caching
      });

      if (useStore && data?.value) {
        buyingListStore.updateBuyinglist({ name }, buyingListId);
      }

      // Handle potential API errors
      if (error.value) {
        console.error('Failed to update buying list:', error.value);
        switch (error.value.statusCode) {
          case 400:
            throw new Error('Missing buying list id or buying list name already exists.');
          case 404:
            throw new Error('BuyingList not found.');
          default:
            throw new Error('Failed to update buying list.');
        }
      }
    } catch (e) {
      const error = e as ApiError;
      console.error('Failed to update buying list:', error.message || error);
      throw new Error('An unexpected error occurred while updating the buying list.');
    }
  };

  /**
   * Delete a buying list by its ID
   */
  const deleteBuyingList = async (
    buyingListId: string,
    useStore = true
  ): Promise<DeleteBuyingListResponse | null> => {
    try {
      await ensureAuthenticated();

      // Call the API to delete the buying list
      const { data, error } = await useFetch<DeleteBuyingListResponse>(
        `/api/buyinglists/${buyingListId}/delete`,
        {
          method: 'DELETE',
          key: `deletebuyingList-${buyingListId}`, // Unique key for caching
        }
      );

      // Handle potential API errors
      if (error.value) {
        console.error('Failed to delete buying list:', error.value);
        throw new Error('Failed to delete buying list.');
      }

      // Optionally update the store
      if (useStore && data.value?.success) {
        buyingListStore.removeBuyinglist(buyingListId);
      }
      return data.value;
    } catch (e) {
      const error = e as ApiError;
      console.error('Failed to delete buying list:', error.message || error);
      throw new Error('An unexpected error occurred while deleting the buying list.');
    }
  };

  /**
   * Initialize orders from buying list
   */
  const initializeOrdersFromBuyingList = async (
    buyingListId: string
  ): Promise<InitializeOrderResponse | null> => {
    try {
      await ensureAuthenticated();

      // Call the API to initialize orders from the buying list
      const { data, error } = await useFetch<InitializeOrderResponse>(
        `/api/buyinglists/${buyingListId}/initializeOrder`,
        {
          method: 'POST',
          key: `initializeOrder-${buyingListId}`, // Unique key for caching
        }
      );

      // Handle potential API errors
      if (error.value) {
        console.error('Failed to initialize orders from buying list:', error.value);
        throw new Error('Failed to initialize orders from buying list.');
      }

      return data.value;
    } catch (e) {
      const error = e as ApiError;
      console.error('Failed to initialize orders from buying list:', error.message || error);
      return null;
    }
  };

  /**
   * Fetch buying list lines by buying list ID
   */
  const getBuyingListItems = async (
    params: GetBuyingListItemsRequest
  ): Promise<GetBuyingListItemsResponse | null> => {
    try {
      await ensureAuthenticated();

      // Call API to fetch buying list lines by buying list ID
      const { data, error } = await useFetch<GetBuyingListItemsResponse>(
        `/api/buyinglists/${params.buyingListId}/items`,
        {
          query: {
            currency: params.currency,
            page: params.pageable.page,
            size: params.pageable.size || defaultPaginationSize,
            supplierId: params.supplierId ? JSON.stringify(params.supplierId) : undefined,
            productVariantId: params.productVariantId
              ? JSON.stringify(params.productVariantId)
              : undefined,
            offerPriceId: params.offerPriceId ? JSON.stringify(params.offerPriceId) : undefined,
          },
          key: `getbuyingListlines-${params.buyingListId}`, // Unique key for caching
        }
      );

      // Handle potential API errors
      if (error.value) {
        console.error('Failed to get buying list lines:', error.value);
        throw new Error('Failed to get buying list lines.');
      }

      // Save data in store
      if (data.value?.success) {
        buyingListStore.buyinglistItems.set(params.buyingListId, data.value.buyingListItems ?? []);
      }

      // Return the response
      return data.value || null;
    } catch (e) {
      const error = e as ApiError;
      console.error('Unexpected error while getting buying list lines:', error.message || error);
      throw new Error('An unexpected error occurred while getting buying list lines.');
    }
  };

  /**
   * Delete buying list lines
   */
  const deleteBuyingListItems = async (
    params: DeleteBuyingListItemsRequest
  ): Promise<DeleteBuyingListItemsResponse | null> => {
    try {
      await ensureAuthenticated();

      // Call the API to delete buying list lines
      const { data, error } = await useFetch<DeleteBuyingListItemsResponse>(
        `/api/buyinglists/${params.buyingListId}/items/delete`,
        {
          method: 'DELETE',
          body: params,
          key: `deleteBuyingListItems-${params.buyingListId}-${params.lineIdsToDelete}`, // Unique key for caching
        }
      );

      // Handle potential API errors
      if (error.value) {
        console.error('Failed to delete buying list lines:', error.value);
        throw new Error('Failed to delete buying list lines.');
      }

      // Update data in store
      if (data.value?.success) {
        buyingListStore.removeBuyinglistItem(params.lineIdsToDelete, params.buyingListId);
      }

      return data.value || null;
    } catch (e) {
      const error = e as ApiError;
      console.error('Failed to delete buying list lines:', error.message || error);
      throw new Error('An unexpected error occurred while deleting buying list lines.');
    }
  };

  /**
   * Update buying list lines
   */
  const updateBuyingListItems = async (
    params: UpdateBuyingListItemsRequest
  ): Promise<UpdateBuyingListItemsResponse | null> => {
    try {
      await ensureAuthenticated();

      // Call the API to update buying list lines
      const { data, error } = await useFetch<UpdateBuyingListItemsResponse>(
        `/api/buyinglists/${params.buyingListId}/items/update`,
        {
          method: 'PUT',
          body: params,
          key: `updateBuyingListItems-${params.buyingListId}`, // Unique key for caching
        }
      );

      // Handle potential API errors
      if (error.value) {
        console.error('Failed to update buying list lines:', error.value);
        switch (error.value.statusCode) {
          case 400:
            throw new Error(
              'Quantity lower or equals to 0 / missing quantity / missing offerPriceId.'
            );
          case 404:
            throw new Error('BuyingList not found.');
          case 412:
            throw new Error('Impossible to add any buying list line.');
          default:
            throw new Error('Failed to update buying list lines.');
        }
      }

      return data.value || null;
    } catch (e) {
      const error = e as ApiError;
      console.error('Failed to update buying list lines:', error.message || error);
      throw new Error('An unexpected error occurred while updating buying list lines.');
    }
  };

  return {
    getBuyingListbyId,
    getBuyingLists,
    duplicateBuyingList,
    addOrUpdateBuyingList,
    createBuyingList,
    updateBuyingList,
    deleteBuyingList,
    initializeOrdersFromBuyingList,
    getBuyingListItems,
    deleteBuyingListItems,
    updateBuyingListItems,
  };
}
